import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";

export const Route = createFileRoute("/roof-inspection")({
  head: () => ({
    meta: [
      { title: "Free Roof Inspection in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Request an honest roof inspection in Citrus County for leaks, storm damage, aging shingles, metal roofs and replacement planning." },
      { property: "og:title", content: "Free Roof Inspection in Citrus County, FL" },
      { property: "og:description", content: "Honest, no-pressure roof inspection with a written estimate." },
      { property: "og:url", content: canonical("/roof-inspection") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/roof-inspection") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Roof Inspection", href: "/roof-inspection" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Roof Inspection",
          serviceType: "Roof Inspection",
          description: "Honest roof inspection with a written estimate for Citrus County, FL homeowners.",
          url: canonical("/roof-inspection"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Roof Inspection", href: "/roof-inspection" }]} />
      <PageHero showRating
        eyebrow="Roof Inspection"
        title="Free Roof Inspection in Citrus County, FL"
        subtitle="An honest walk-through of your roof with a written estimate — no pressure, no scare tactics. That's how a local family-owned roofer should work."
      />
      <ServicePageBody title="Roof Inspection" service="Roof Inspection">
        <h2>When you need a roof inspection</h2>
        <p>
          A roof does not have to be leaking to need attention. Homeowners call us for inspections after a storm, before buying or selling a home, when insurance requests a wind-mitigation or four-point report, or simply because the roof is aging and they want a straight answer about how much life is left in it.
        </p>

        <h2>What we look at during a Destination Roofing inspection</h2>
        <ul>
          <li><strong>Shingles or roofing material</strong> — granule loss, lifted or missing shingles, sealant condition, exposed fasteners on metal panels.</li>
          <li><strong>Flashings, valleys and penetrations</strong> — around chimneys, plumbing vents, skylights and pipe boots.</li>
          <li><strong>Visible decking and structure</strong> — soft spots, sagging areas, previously repaired sections.</li>
          <li><strong>Gutters, soffit and fascia</strong> — signs of water backing up behind the roof edge.</li>
          <li><strong>Attic ventilation</strong> when accessible and within our normal inspection process.</li>
          <li><strong>Storm-damage documentation</strong> — photos of anything relevant to a possible insurance claim.</li>
        </ul>

        <h2>Repair or replace?</h2>
        <p>
          If we can save you money by repairing your roof rather than replacing it, we'll tell you. If your roof is at end of life and repeated repairs are throwing money at the problem, we'll tell you that too. Sometimes the honest answer is "your roof is fine — call us in three years." That happens more often than most homeowners expect.
        </p>

        <h2>What you receive after the inspection</h2>
        <ul>
          <li>A plain-English summary of what we saw</li>
          <li>A written estimate for any recommended work</li>
          <li>Photos of anything meaningful we found</li>
          <li>Time to ask questions and decide on your own schedule</li>
        </ul>

        <h2>Related services</h2>
        <p>
          Depending on what we find, you may want to read about <Link to="/roof-repair">roof repair</Link>, <Link to="/roof-replacement">roof replacement</Link>, or <Link to="/storm-damage-roof-repair">storm damage roof repair</Link>. If you're planning a new roof, our <Link to="/atlas-shingle-colors">Atlas shingle-color visualizer</Link> can help you preview colors on your home before you commit.
        </p>
      </ServicePageBody>
    </>
  );
}
